import React, {useRef, useEffect, useState, useCallback} from "react";
import {Trash2, Edit, Loader, Tag} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {useInfiniteQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {SettingsToolbar, SelectionBar, SettingsTable, StatusBadge, AddAction, ListLoader} from "./SettingsUI";

const ClientTypesList = ({
                       searchTerm,
                       setSearchTerm,
                       debouncedSearchTerm,
                       selectedItems,
                       setSelectedItems,
                       selectedAll,
                       setSelectedAll,
                       showNotification,
                       onCreateClientType,
                       onEditClientType
                   }) => {
    const queryClient = useQueryClient();
    const loadingRef = useRef(null);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pendingDelete, setPendingDelete] = useState(null);


    // Function to fetch client types from the API with pagination and filtering
    const fetchClientTypes = async ({pageParam = 1}) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Build the query string with pagination and name filter
            let url = `/client-types?page=${pageParam}`;
            if (debouncedSearchTerm.trim()) {
                url += `&name=${encodeURIComponent(debouncedSearchTerm.trim())}`;
            }

            const response = await api.get(url, {
                headers: {
                    'X-account-id': accountId.id,
                    'Accept': 'application/json'
                }
            });

            return {
                data: response.data.items || [],
                currentPage: response.data.currentPage,
                totalPages: response.data.totalPages,
                hasNextPage: response.data.currentPage < response.data.totalPages
            };
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                "Une erreur lors de la récupération des types de client";
            throw new Error(errorMessage);
        }
    };

    // Function to delete client types
    const deleteClientTypes = async (clientTypeIds) => {
        try {
            const accountId = await secureStorage.getItem("selectedAccount");

            // Delete client types one by one
            const results = await Promise.all(
                clientTypeIds.map(id =>
                    api.delete(`/client-types/${id}`, {
                        headers: {
                            'X-account-id': accountId.id,
                            'Accept': 'application/json'
                        }
                    }).catch(err => {
                        return {error: err, id};
                    })
                )
            );

            // Check for errors
            const failures = results.filter(r => r && r.error);
            if (failures.length > 0) {
                if (failures.length === clientTypeIds.length) {
                    throw new Error("Impossible de supprimer les types de client sélectionnés");
                } else {
                    throw new Error(`${failures.length} sur ${clientTypeIds.length} types de client n'ont pas pu être supprimés`);
                }
            }

            return clientTypeIds;
        } catch (error) {
            const errorMessage = error.response?.data?.detail ||
                error.response?.data?.message ||
                error.message ||
                "Une erreur lors de la suppression des types de client";
            throw new Error(errorMessage);
        }
    };

    // Use react-query for delete mutation
    const deleteMutation = useMutation({
        mutationFn: deleteClientTypes,
        onMutate: () => {
            showNotification('loading', 'Suppression en cours...');
        },
        onSuccess: () => {
            // Reset selection and refetch data
            setSelectedItems([]);
            setSelectedAll(false);
            queryClient.invalidateQueries({queryKey: ['client-types']});
            showNotification('success', 'Types de client supprimés avec succès');
        },
        onError: (error) => {
            showNotification('error', `Erreur: ${error.message}`);
        }
    });

    // Use react-query to fetch and cache the data with infinite query
    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isLoading,
        error
    } = useInfiniteQuery({
        queryKey: ['client-types', debouncedSearchTerm],
        queryFn: fetchClientTypes,
        getNextPageParam: (lastPage) => {
            return lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined;
        },
    });

    // Flatten the pages data into a single client types array
    const clientTypes = data?.pages.flatMap(page => page.data) || [];

    // Function to load more data
    const loadMore = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            setIsLoadingMore(true);
            fetchNextPage().finally(() => setIsLoadingMore(false));
        }
    }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

    // Implement Intersection Observer for infinite scrolling
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) {
                    loadMore();
                }
            },
            {threshold: 0.5}
        );

        if (loadingRef.current) {
            observer.observe(loadingRef.current);
        }

        return () => {
            if (loadingRef.current) {
                observer.unobserve(loadingRef.current);
            }
        };
    }, [loadMore]);

    // Reset when search term changes
    useEffect(() => {
        setSelectedItems([]);
        setSelectedAll(false);
    }, [debouncedSearchTerm, setSelectedItems, setSelectedAll]);

    // Handle select all checkbox
    const handleSelectAll = () => {
        const newSelectedAll = !selectedAll;
        setSelectedAll(newSelectedAll);
        setSelectedItems(newSelectedAll ? clientTypes.map(item => item.id) : []);
    };

    // Handle individual item selection
    const toggleItem = (clientTypeId) => {
        setSelectedItems((prevSelected) =>
            prevSelected.includes(clientTypeId)
                ? prevSelected.filter((id) => id !== clientTypeId)
                : [...prevSelected, clientTypeId]
        );
    };

    // ids awaiting confirmation: the selection, or a single row from its menu
    const askDelete = (ids) => setPendingDelete(ids);

    const confirmDelete = () => {
        deleteMutation.mutate(pendingDelete);
        setPendingDelete(null);
    };

    const rowActions = (clientType) => [
        {label: 'Modifier', icon: Edit, onClick: () => onEditClientType(clientType), tone: 'blue'},
        {label: 'Supprimer', icon: Trash2, onClick: () => askDelete([clientType.id]), tone: 'red', danger: true},
    ];

    const columns = [
        {key: 'name', label: 'Nom', render: (t) => t.name},
        {key: 'active', label: 'Statut', render: (t) => <StatusBadge active={t.active}/>},
    ];

    return (
        <div>
            <AddAction onClick={onCreateClientType}/>
            <SettingsToolbar search={searchTerm} onSearch={setSearchTerm} placeholder="Rechercher un type de client..."/>

            <SelectionBar count={selectedItems.length} singular="type de client sélectionné" plural="types de client sélectionnés"
                          onDelete={() => askDelete(selectedItems)} deleting={deleteMutation.isPending}/>

            {isLoading && !data ? (
                <ListLoader label="Chargement des types de client..."/>
            ) : error && !data ? (
                <div className="p-4 text-center text-red-500">Erreur : {error.message}</div>
            ) : (
                <SettingsTable
                    columns={columns}
                    rows={clientTypes}
                    selection={{selected: selectedItems, onToggle: toggleItem, allSelected: selectedAll, onToggleAll: handleSelectAll}}
                    rowActions={rowActions}
                    onRowClick={onEditClientType}
                    empty={{
                        icon: Tag,
                        title: debouncedSearchTerm ? 'Aucun type de client trouvé' : 'Aucun type de client',
                        text: debouncedSearchTerm ? 'Ajustez la recherche.' : 'Créez des types pour classer vos clients.',
                    }}
                >
                    {(hasNextPage || isFetchingNextPage) && (
                        <div ref={loadingRef} className="flex justify-center items-center py-4">
                            <Loader className="w-6 h-6 text-primary animate-spin"/>
                        </div>
                    )}
                </SettingsTable>
            )}

            {pendingDelete && (
                <ConfirmationDialog
                    title="Confirmation de suppression"
                    message={`Êtes-vous sûr de vouloir supprimer ${pendingDelete.length} type de client${pendingDelete.length > 1 ? 's' : ''} ? Cette action est irréversible.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    onConfirm={confirmDelete}
                    onCancel={() => setPendingDelete(null)}
                    danger={true}
                />
            )}
        </div>
    );
};

export default ClientTypesList;
